import { measureTextWidth } from "src/utils/measureTextWidth";

export type RecipeLayout = "inline" | "stacked";

export const RECIPE_LINE_HEIGHT = 1.15;
export const RECIPE_PADDING_Y_EM = 0.1;
export const RECIPE_PADDING_X_EM = 0.25;
export const RECIPE_COLUMN_GAP_EM = 0.75;
export const MIN_RECIPE_FONT_SIZE = 48;
export const MAX_RECIPE_FONT_SIZE = 160;

const RECIPE_BORDER_PX = 1;
// Canvas widths can differ a little from the rendered text, for example before
// the web font loads.
const WIDTH_FIT_MARGIN = 0.92;
const HEIGHT_FIT_MARGIN = 0.98;
// A material name and its count read best on one line, so stack them only for
// a clear gain in size.
const MIN_STACKED_GAIN = 1.15;
const MAX_STACKED_VALUE_TO_LABEL = 1.4;
const MEASURING_FONT_SIZE = 100;
const FALLBACK_CHARACTER_WIDTH_EM = 0.6;

export const widestTextEm = ({
  texts,
  fontWeight,
  fontFamily,
}: {
  texts: string[];
  fontWeight: string;
  fontFamily: string;
}) =>
  Math.max(
    0,
    ...texts.map(
      (text) =>
        (measureTextWidth({
          text,
          font: `${fontWeight} ${MEASURING_FONT_SIZE.toString()}px ${fontFamily}`,
        }) ?? text.length * FALLBACK_CHARACTER_WIDTH_EM * MEASURING_FONT_SIZE) /
        MEASURING_FONT_SIZE
    )
  );

const toFontSize = (fittedFontSize: number) =>
  Math.min(
    MAX_RECIPE_FONT_SIZE,
    Math.max(MIN_RECIPE_FONT_SIZE, Math.floor(fittedFontSize))
  );

export const fitRecipeText = ({
  width,
  height,
  lineCount,
  widestLabelEm,
  widestValueEm,
}: {
  width: number;
  height: number;
  lineCount: number;
  widestLabelEm: number;
  widestValueEm: number;
}): { layout: RecipeLayout; labelFontSize: number; valueFontSize: number } => {
  const heightPerLine =
    ((height - lineCount * RECIPE_BORDER_PX) * HEIGHT_FIT_MARGIN) / lineCount;
  const textWidth = width * WIDTH_FIT_MARGIN;
  const inlineFontSize = Math.min(
    heightPerLine / (RECIPE_LINE_HEIGHT + 2 * RECIPE_PADDING_Y_EM),
    textWidth /
      (widestLabelEm +
        widestValueEm +
        RECIPE_COLUMN_GAP_EM +
        2 * RECIPE_PADDING_X_EM)
  );
  const stackedPartHeightEm = RECIPE_LINE_HEIGHT + RECIPE_PADDING_Y_EM;
  const stackedLabelFontSize = Math.min(
    heightPerLine / (2 * stackedPartHeightEm),
    textWidth / (widestLabelEm + 2 * RECIPE_PADDING_X_EM)
  );
  const stackedValueFontSize = Math.min(
    (heightPerLine - stackedLabelFontSize * stackedPartHeightEm) /
      stackedPartHeightEm,
    textWidth / (widestValueEm + 2 * RECIPE_PADDING_X_EM),
    stackedLabelFontSize * MAX_STACKED_VALUE_TO_LABEL
  );
  if (
    Math.min(stackedLabelFontSize, stackedValueFontSize) >=
    inlineFontSize * MIN_STACKED_GAIN
  ) {
    return {
      layout: "stacked",
      labelFontSize: toFontSize(stackedLabelFontSize),
      valueFontSize: toFontSize(stackedValueFontSize),
    };
  }
  return {
    layout: "inline",
    labelFontSize: toFontSize(inlineFontSize),
    valueFontSize: toFontSize(inlineFontSize),
  };
};
